import type { ObservationModel } from '@ctw/shared/api/fhir/models/observation';
import type { VitalsBucket } from '@ctw/shared/api/fhir/models/vitals-bucket';
import { usePatientVitals } from '@ctw/shared/api/fhir/vitals';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useHotloadReady } from '@ctw/shared/hooks/use-hotload-ready';
import { orderBy } from 'lodash-es';
import { useMemo } from 'react';

export type OverviewVital = {
  vital: string;
  value?: string;
  date?: string;
  firstParty?: boolean;
};

export interface PatientVitalsOverviewData {
  data: Array<OverviewVital>;
  isLoading: boolean;
  hasNoRecords: boolean;
}

const OVERVIEW_VITALS = [
  { title: 'BP', dataIndex: 'bloodPressure' },
  { title: 'HR', dataIndex: 'pulse' },
  { title: 'Temp', dataIndex: 'temperature' },
  { title: 'RR', dataIndex: 'respiratoryRate' },
  { title: 'SpO2', dataIndex: 'oxygenSaturation' },
  { title: 'Ht/Lt', dataIndex: 'height' },
  { title: 'Wt', dataIndex: 'weight' },
  { title: 'BMI', dataIndex: 'bmi' },
  { title: 'HC', dataIndex: 'headCircumference' },
  { title: 'LMP', dataIndex: 'lmp' },
  { title: 'Pain', dataIndex: 'pain' },
] as Array<{ title: string; dataIndex: keyof VitalsBucket }>;

const VITALS_TO_DROP_WHEN_BLANK = ['HC', 'LMP', 'Pain'];

/**
 * Transforms the patient's vitals into the most-recent-per-vital buckets shown
 * in the overview card. Unlike the other overview data hooks, `data` is a list
 * of display buckets rather than a list of FHIR resource models.
 */
export function usePatientVitalsOverviewData(): PatientVitalsOverviewData {
  const { patient } = usePatientContext();
  const ready = useHotloadReady({ upid: patient.UPID, resourceTypes: ['Observation'] });
  const { data, isLoading } = usePatientVitals(ready);

  const overviewVitals = useMemo<Array<OverviewVital>>(() => {
    if (isLoading || data?.length === 0) {
      return [];
    }

    // Get newest encounters first, so that we grab most recent vitals first.
    const sortedBuckets = orderBy(data, (e) => e.dateTime, 'desc');

    // For each vital, find the most recent encounter that has that vital.
    const vitals: Array<OverviewVital> = OVERVIEW_VITALS.map((vital) => {
      const vitalBucket = sortedBuckets.find((e) => e[vital.dataIndex]);
      const vitalData = vitalBucket?.[vital.dataIndex] as
        // BP has a different structure from the other vitals
          | {
              display: string;
              isFirstParty: boolean;
            }
          | {
              isFirstParty: boolean;
              observation: ObservationModel;
            }
          | undefined;
      let value: string | undefined;

      if (vitalData) {
        value = 'display' in vitalData ? vitalData.display : vitalData?.observation?.valueWithUnits;
      }

      return {
        vital: vital.title,
        value,
        date: vitalBucket?.dateDisplay,
        firstParty: vitalData?.isFirstParty,
      };
    });

    // Filter out some of our empty vitals.
    return vitals.filter(
      (v) => !VITALS_TO_DROP_WHEN_BLANK.includes(v?.vital ?? '') || v?.value !== undefined,
    );
  }, [data, isLoading]);

  return {
    data: overviewVitals,
    isLoading: !ready || isLoading,
    hasNoRecords: data?.length === 0,
  };
}
